'use client';
import { createAskInputDraftRepository } from '#pplx/frontend/apps/ask/ask-input/ask-input-drafts/askInputDraftRepository.js';
import { createAskInputDraftSession } from '#pplx/frontend/apps/ask/ask-input/ask-input-drafts/askInputDraftSession.js';
import { useIsIncognito } from '#pplx/frontend/apps/ask/incognito/useIsIncognito.js';
import { useSession } from '#pplx/frontend/auth/useSession.js';
import { logger } from '#pplx/frontend/logger/browser.js';
import { useCallback, useEffect, useMemo, useRef, useSyncExternalStore } from 'react';
const subscribeToNothing = () => () => { };
const getEmptyDraftsSnapshot = () => undefined;
export function createPlainTextEditorState(text) {
    const paragraphs = text.split('\n').map((line) => {
        const children = line
            ? [
                {
                    detail: 0,
                    format: 0,
                    mode: 'normal',
                    style: '',
                    text: line,
                    type: 'text',
                    version: 1,
                },
            ]
            : [];
        return {
            children,
            direction: 'ltr',
            format: '',
            indent: 0,
            textFormat: 0,
            textStyle: '',
            type: 'paragraph',
            version: 1,
        };
    });
    return {
        root: {
            children: paragraphs,
            direction: 'ltr',
            format: '',
            indent: 0,
            type: 'root',
            version: 1,
        },
    };
}
function cleanupDraftSession(draftSession, shouldPersistActiveDraft) {
    if (shouldPersistActiveDraft) {
        void draftSession?.flush();
        return;
    }
    void draftSession?.discardActiveDraft();
}
export function useDraftSession({ accountId, context, enabled, currentSnapshot, }) {
    const enabledRef = useRef(enabled);
    enabledRef.current = enabled;
    const draftSession = useMemo(() => {
        if (!enabled || !accountId || !context)
            return undefined;
        const repository = createAskInputDraftRepository(accountId);
        if (!repository) {
            logger.warn('Unable to create an account-scoped Ask input draft store');
            return undefined;
        }
        return createAskInputDraftSession({
            repository,
            context,
            onError: (error) => logger.warn('Ask input draft storage failed', { error }),
        });
    }, [accountId, context, enabled]);
    const drafts = useSyncExternalStore(draftSession?.subscribe ?? subscribeToNothing, draftSession?.getDraftsSnapshot ?? getEmptyDraftsSnapshot, getEmptyDraftsSnapshot);
    const draftIdentityWasReadyRef = useRef(!!accountId && !!context);
    useEffect(() => {
        const draftIdentityIsReady = !!accountId && !!context;
        const shouldSeedCurrentInput = enabled && !draftIdentityWasReadyRef.current;
        if (draftIdentityIsReady)
            draftIdentityWasReadyRef.current = true;
        if (shouldSeedCurrentInput)
            draftSession?.updateDraft(currentSnapshot);
    }, [accountId, context, currentSnapshot, draftSession, enabled]);
    useEffect(() => () => cleanupDraftSession(draftSession, enabledRef.current), [draftSession]);
    const updateDraft = useCallback((snapshot) => draftSession?.updateDraft(snapshot), [draftSession]);
    const discardActiveDraft = useCallback(() => {
        void draftSession?.discardActiveDraft();
    }, [draftSession]);
    const activateDraft = useCallback(async (draft) => draftSession?.activateDraft(draft), [draftSession]);
    const deleteDraft = useCallback(async (uuid) => draftSession?.deleteDraft(uuid), [draftSession]);
    const clearDrafts = useCallback(async () => (await draftSession?.clearDrafts()) ?? false, [draftSession]);
    return {
        updateDraft,
        activateDraft,
        discardActiveDraft,
        deleteDraft,
        clearDrafts,
        drafts,
    };
}
export function useThreadFollowupDrafts({ threadUuid, surfaceEnabled, value, lexicalJson, actions, }) {
    const { session } = useSession();
    const accountId = session?.user?.id;
    const { isIncognito } = useIsIncognito({ reason: 'thread-followup-drafts' });
    const context = useMemo(() => threadUuid
        ? {
            kind: 'thread-followup',
            threadUuid,
        }
        : undefined, [threadUuid]);
    const enabled = surfaceEnabled && !!accountId && !!context && !isIncognito;
    const currentSnapshot = useMemo(() => value.trim()
        ? {
            text: value,
            lexicalJson: lexicalJson ?? createPlainTextEditorState(value),
        }
        : undefined, [lexicalJson, value]);
    const { updateDraft, activateDraft, discardActiveDraft, deleteDraft, clearDrafts, drafts } = useDraftSession({
        accountId,
        context,
        currentSnapshot,
        enabled,
    });
    const handleChange = useCallback((text, lexicalJson) => {
        actions.onChange(text, lexicalJson);
        updateDraft(text.trim()
            ? { text, lexicalJson: lexicalJson ?? createPlainTextEditorState(text) }
            : undefined);
    }, [actions, updateDraft]);
    const handleSetUserInput = useCallback((text, lexicalJson) => {
        if (lexicalJson)
            actions.setUserInput(text, lexicalJson);
        else
            actions.setUserInput(text);
        updateDraft(text.trim()
            ? { text, lexicalJson: lexicalJson ?? createPlainTextEditorState(text) }
            : undefined);
    }, [actions, updateDraft]);
    const handleSubmit = useCallback((params) => {
        const didSubmit = actions.onSubmit(params);
        if (didSubmit)
            discardActiveDraft();
        return didSubmit;
    }, [actions, discardActiveDraft]);
    const handleSelectDraft = useCallback(async (draft) => {
        await activateDraft(draft);
        handleSetUserInput(draft.snapshot.text, draft.snapshot.lexicalJson);
    }, [activateDraft, handleSetUserInput]);
    const draftAwareActions = useMemo(() => ({
        onChange: handleChange,
        onSubmit: handleSubmit,
        setUserInput: handleSetUserInput,
    }), [handleChange, handleSetUserInput, handleSubmit]);
    const controlProps = useMemo(() => enabled
        ? {
            drafts,
            deleteDraft,
            clearDrafts,
            onSelectDraft: handleSelectDraft,
        }
        : undefined, [clearDrafts, deleteDraft, drafts, enabled, handleSelectDraft]);
    return {
        actions: draftAwareActions,
        controlProps,
    };
}
//# sourceMappingURL=useThreadFollowupDrafts.js.map